"use client";

import Image from "next/image";
import { Link } from "@/i18n/routing";
import { authorRoute } from "@/lib/routes";
import { useState } from "react";
import { useToggleToast } from "@/lib/toast";
import { EJHEALTH_COLORS, EJHEALTH_TYPE } from "@/lib/theme/ej-health";
import type { ARTICLE } from "@/fixtures/ej-health/article-detail/fixtures";

type Author = (typeof ARTICLE)["author"];

export function AuthorBio({ author }: { author: Author }) {
  const [following, setFollowing] = useState(false);
  const notify = useToggleToast();
  return (
    <div
      className="flex flex-col gap-5 p-6 sm:flex-row sm:items-start sm:gap-6 sm:p-8"
      style={{
        backgroundColor: EJHEALTH_COLORS.primarySoft,
        borderLeft: `3px solid ${EJHEALTH_COLORS.primary}`,
      }}
    >
      {/* The card's avatar and name open the author's page on the main site,
          exactly as the byline above the article does — QA filed it twice, once
          for the byline and once for this card. */}
      <Link
        href={authorRoute(author.name)}
        aria-label={author.name}
        className="group hkej-tap relative shrink-0 overflow-hidden rounded-full"
        style={{
          width: 80,
          height: 80,
          backgroundColor: EJHEALTH_COLORS.surface,
        }}
      >
        <Image src={author.avatar} alt={author.name} fill className="object-cover" unoptimized />
      </Link>
      <div className="flex flex-1 flex-col gap-2">
        <span
          style={{
            ...EJHEALTH_TYPE.eyebrow,
            color: EJHEALTH_COLORS.primary,
            letterSpacing: "0.18em",
          }}
        >
          專欄作者
        </span>
        <h3>
          <Link
            href={authorRoute(author.name)}
            className="group hkej-tap hkej-tap-title hkej-tap-self"
            style={{
              ...EJHEALTH_TYPE.h3Card,
              color: EJHEALTH_COLORS.dark,
              fontSize: 22,
              lineHeight: "28px",
            }}
          >
            {author.name}
          </Link>
        </h3>
        <p
          style={{
            ...EJHEALTH_TYPE.body,
            color: EJHEALTH_COLORS.textBody,
            fontSize: 14,
            lineHeight: "22px",
          }}
        >
          {author.title}
        </p>
        <div className="flex flex-wrap items-center gap-4 pt-1">
          <span style={{ ...EJHEALTH_TYPE.meta, color: EJHEALTH_COLORS.textMuted }}>
            年資 10 年
          </span>
          <span style={{ width: 1, height: 12, backgroundColor: EJHEALTH_COLORS.divider }} />
          <span style={{ ...EJHEALTH_TYPE.meta, color: EJHEALTH_COLORS.textMuted }}>
            {author.articles}
          </span>
        </div>
      </div>
      <button
        type="button"
        aria-pressed={following}
        // `next` outside the updater: React runs updaters twice under
        // StrictMode, so a toast raised inside one fires twice. Same note as
        // `ej-tech/shared/follow-button.tsx` and `lib/toast.ts`.
        onClick={() => {
          const next = !following;
          setFollowing(next);
          notify.follow(next);
        }}
        // Resting colours on custom properties, not inline: an inline
        // declaration outranks every `hover:` utility, so this had
        // `transition-colors` and nothing to transition. QA: "add hover effect
        // to the buttons".
        className="inline-flex shrink-0 items-center border-2 border-[color:var(--rest-border)] bg-[color:var(--rest-bg)] px-5 py-2.5 text-[color:var(--rest-fg)] transition-colors duration-150 hover:border-[color:var(--hover-bg)] hover:bg-[color:var(--hover-bg)] hover:text-[color:var(--hover-fg)] motion-reduce:transition-none sm:self-center"
        style={
          {
            ...EJHEALTH_TYPE.button,
            ["--rest-border" as string]: EJHEALTH_COLORS.primary,
            ["--rest-bg" as string]: following
              ? EJHEALTH_COLORS.surface
              : EJHEALTH_COLORS.primary,
            ["--rest-fg" as string]: following
              ? EJHEALTH_COLORS.primary
              : EJHEALTH_COLORS.textOnDark,
            ["--hover-bg" as string]: EJHEALTH_COLORS.primaryDark,
            ["--hover-fg" as string]: EJHEALTH_COLORS.textOnDark,
          } as React.CSSProperties
        }
      >
        {following ? "已關注" : "＋ 關注"}
      </button>
    </div>
  );
}
